/**
 * GDC Chocolate Promo — flavour-choice popup at checkout
 * -------------------------------------------------------
 * Drop this file in as a theme asset (e.g. assets/choc-promo-popup.js)
 * and include it before </body> in theme.liquid:
 *
 *   <script src="{{ 'choc-promo-popup.js' | asset_url }}" defer></script>
 *
 * HOW IT WORKS
 * 1. When the customer clicks any "Checkout" button/link, we intercept the click.
 * 2. We check the live cart for how many eligible products are in it vs how many
 *    chocolates are already in the cart.
 * 3. If they're owed chocolates, we show a popup asking for flavour(s).
 * 4. On confirm, we add the chosen chocolate variants via /cart/add.js, then
 *    continue on to checkout automatically.
 * 5. If nothing is owed, the checkout click passes through untouched.
 *
 * CONFIG — fill these in with your real product/variant IDs before going live.
 * Find IDs in Shopify admin: Products > [product] — the ID is in the URL,
 * or use the "..." menu > "View JSON" trick, or Shopify GraphiQL.
 */
const CHOC_PROMO_CONFIG = {
  // Product IDs (not variant IDs) of the 3 products that earn a free chocolate.
  eligibleProductIds: [
    8016792256818, // 750ml Tweed Martini Connoisseur Premium Gin
    8342741287218, // 750ml Apricity Premium Gin
    9956495130930, // 750ml Dark Sky Reserve Gin
  ],

  // The two chocolate flavours — each is its own PRODUCT with no visible
  // variants in admin. Shopify still auto-creates one variant behind the
  // scenes for every product (needed because /cart/add.js always adds by
  // variant ID, never product ID directly).
  //
  // To find the variantId for a no-variant product: visit
  //   https://[your-store-domain]/products/[product-handle].js
  // in a browser — it's a public JSON endpoint, no login needed — and copy
  // the "id" field inside the "variants" array (there'll only be one).
  chocolateFlavours: [
    {
      productId: 9916496216370,
      variantId: 50226291966258,
      title: "Premium Gin Dark Chocolate",
    },
    {
      productId: 9479697006898,
      variantId: 50137919324466,
      title: "Premium Gin Milk Chocolate",
    },
  ],

  // Discount code to auto-apply once chocolates are added.
  // Must match a Shopify discount code exactly (case-sensitive).
  discountCode: "CHOCPROMO", // TODO: replace with your real discount code

  // Extra CSS selectors for your theme's own checkout buttons, if the
  // defaults below don't catch everything (e.g. a custom cart drawer button).
  // Example: ['.cart-drawer__checkout', '#CartDrawer-Checkout']
  extraCheckoutSelectors: [],

  // Optional: copy customisation
  copy: {
    heading: "Choose your free chocolate",
    subheading: (count) =>
      `You've earned ${count} free chocolate${count > 1 ? "s" : ""} — pick your flavour${count > 1 ? "s" : ""} before checkout.`,
    confirmButton: "Continue to checkout",
    cancelButton: "Back to cart",
  },
};

(function () {
  "use strict";

  const CFG = CHOC_PROMO_CONFIG;

  const DEFAULT_CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'a[href="/checkout"]',
    'input[name="checkout"]',
    "#checkout",
  ];

  const CHECKOUT_SELECTORS = DEFAULT_CHECKOUT_SELECTORS.concat(
    CFG.extraCheckoutSelectors || []
  );

  let modalEl = null;
  let isProcessing = false; // guards against double-submits / re-entrant clicks

  // ---------- Cart helpers ----------

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Failed to load cart");
    return res.json();
  }

  function computeRemaining(cart) {
    let eligibleQty = 0;
    let chocolateQty = 0;

    const chocolateProductIds = CFG.chocolateFlavours.map((f) => f.productId);

    for (const item of cart.items) {
      if (CFG.eligibleProductIds.includes(item.product_id)) {
        eligibleQty += item.quantity;
      }
      if (chocolateProductIds.includes(item.product_id)) {
        chocolateQty += item.quantity;
      }
    }

    return Math.max(0, eligibleQty - chocolateQty);
  }

  async function addChocolates(selections) {
    // selections: { [variantId]: quantity }
    const items = Object.entries(selections)
      .filter(([, qty]) => qty > 0)
      .map(([variantId, qty]) => ({ id: Number(variantId), quantity: qty }));

    if (items.length === 0) return;

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || "Could not add chocolate to cart");
    }
  }

  // ---------- Modal ----------

  function buildModal(remaining) {
    const modal = document.createElement("div");
    modal.className = "choc-promo-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "choc-promo-heading");

    const flavourRows = CFG.chocolateFlavours
      .map(
        (f) => `
        <div class="choc-promo-row" data-variant-id="${f.variantId}">
          <span class="choc-promo-row__label">${f.title}</span>
          <div class="choc-promo-stepper">
            <button type="button" class="choc-promo-stepper__btn" data-action="decrement" aria-label="Decrease ${f.title}">–</button>
            <span class="choc-promo-stepper__count" data-count>0</span>
            <button type="button" class="choc-promo-stepper__btn" data-action="increment" aria-label="Increase ${f.title}">+</button>
          </div>
        </div>`
      )
      .join("");

    modal.innerHTML = `
      <div class="choc-promo-modal__backdrop" data-close></div>
      <div class="choc-promo-modal__card">
        <button type="button" class="choc-promo-modal__close" data-close aria-label="Close">&times;</button>
        <p class="choc-promo-modal__eyebrow">A gift from GDC</p>
        <h2 id="choc-promo-heading" class="choc-promo-modal__heading">${CFG.copy.heading}</h2>
        <hr class="choc-promo-modal__heading-rule" />
        <p class="choc-promo-modal__subheading">${CFG.copy.subheading(remaining)}</p>
        <div class="choc-promo-rows">${flavourRows}</div>
        <p class="choc-promo-modal__remaining" data-remaining-text></p>
        <div class="choc-promo-modal__actions">
          <button type="button" class="choc-promo-btn choc-promo-btn--secondary" data-cancel>${CFG.copy.cancelButton}</button>
          <button type="button" class="choc-promo-btn choc-promo-btn--primary" data-confirm disabled>${CFG.copy.confirmButton}</button>
        </div>
      </div>
    `;

    return modal;
  }

  function injectStyles() {
    if (document.getElementById("choc-promo-styles")) return;
    const style = document.createElement("style");
    style.id = "choc-promo-styles";
    style.textContent = `
      :root {
        --gdc-ink: #1c1a17;
        --gdc-cream: #f6f1e7;
        --gdc-cream-deep: #ede4d1;
        --gdc-gold: #a9803f;
        --gdc-gold-dark: #8a6830;
        --gdc-line: #ddd0b4;
      }
      .choc-promo-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .choc-promo-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(20, 16, 10, 0.6);
      }
      .choc-promo-modal__card {
        position: relative;
        background: var(--gdc-cream);
        border: 1px solid var(--gdc-line);
        border-radius: 2px;
        padding: 36px 32px 28px;
        width: min(440px, 90vw);
        max-height: 85vh;
        overflow-y: auto;
        box-shadow: 0 20px 48px rgba(20, 16, 10, 0.35);
        font-family: inherit;
        color: var(--gdc-ink);
      }
      .choc-promo-modal__card::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, var(--gdc-gold), var(--gdc-gold-dark));
      }
      .choc-promo-modal__close {
        position: absolute;
        top: 14px;
        right: 16px;
        background: none;
        border: none;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        color: var(--gdc-ink);
        opacity: 0.5;
        transition: opacity 0.15s ease;
      }
      .choc-promo-modal__close:hover {
        opacity: 1;
      }
      .choc-promo-modal__eyebrow {
        text-transform: uppercase;
        letter-spacing: 0.14em;
        font-size: 1rem;
        color: var(--gdc-gold-dark);
        margin: 0 0 8px;
        font-weight: 600;
      }
      .choc-promo-modal__heading {
        margin: 0 0 10px;
        font-family: Georgia, 'Times New Roman', serif;
        font-weight: 500;
        font-size: 1.75rem;
        letter-spacing: 0.01em;
        line-height: 1.25;
      }
      .choc-promo-modal__heading-rule {
        width: 40px;
        height: 2px;
        background: var(--gdc-gold);
        border: none;
        margin: 0 0 16px;
      }
      .choc-promo-modal__subheading {
        margin: 0 0 22px;
        color: var(--gdc-ink);
        opacity: 0.72;
        font-size: 1.5rem;
        line-height: 1.5;
      }
      .choc-promo-rows {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 8px;
      }
      .choc-promo-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #fff;
        border: 1px solid var(--gdc-line);
        border-radius: 2px;
        padding: 14px 16px;
      }
      .choc-promo-row__label {
        font-size: 1.2rem;
        letter-spacing: 0.01em;
      }
      .choc-promo-stepper {
        display: flex;
        align-items: center;
        gap: 14px;
      }
      .choc-promo-stepper__btn {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 1px solid var(--gdc-gold);
        background: transparent;
        color: var(--gdc-gold-dark);
        cursor: pointer;
        font-size: 1.1rem;
        line-height: 1;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .choc-promo-stepper__btn:hover:not(:disabled) {
        background: var(--gdc-gold);
        color: #fff;
      }
      .choc-promo-stepper__btn:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      .choc-promo-stepper__count {
        min-width: 18px;
        text-align: center;
        font-variant-numeric: tabular-nums;
        font-size: 1.05rem;
      }
      .choc-promo-modal__remaining {
        margin: 14px 0 20px;
        font-size: 1.2rem;
        letter-spacing: 0.01em;
        color: var(--gdc-ink);
        opacity: 0.6;
      }
      .choc-promo-modal__remaining.is-balanced {
        color: #4a6b45;
        opacity: 1;
      }
      .choc-promo-modal__actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
      }
      .choc-promo-btn {
        border-radius: 999px;
        padding: 12px 24px;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        cursor: pointer;
        border: 1px solid transparent;
        transition: opacity 0.15s ease, background 0.15s ease;
      }
      .choc-promo-btn--secondary {
        background: transparent;
        border-color: var(--gdc-line);
        color: var(--gdc-ink);
      }
      .choc-promo-btn--secondary:hover {
        background: var(--gdc-cream-deep);
      }
      .choc-promo-btn--primary {
        background: var(--gdc-ink);
        color: var(--gdc-cream);
      }
      .choc-promo-btn--primary:hover:not(:disabled) {
        background: var(--gdc-gold-dark);
      }
      .choc-promo-btn--primary:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  function showModal(remaining) {
    return new Promise((resolve) => {
      injectStyles();
      const modal = buildModal(remaining);
      document.body.appendChild(modal);
      modalEl = modal;
      document.body.style.overflow = "hidden";

      const rows = Array.from(modal.querySelectorAll(".choc-promo-row"));
      const remainingText = modal.querySelector("[data-remaining-text]");
      const confirmBtn = modal.querySelector("[data-confirm]");
      const counts = {};
      rows.forEach((row) => (counts[row.dataset.variantId] = 0));

      function totalSelected() {
        return Object.values(counts).reduce((a, b) => a + b, 0);
      }

      function refresh() {
        const total = totalSelected();
        const left = remaining - total;
        remainingText.textContent =
          left === 0
            ? "All set — ready to go."
            : left > 0
            ? `${left} more to choose.`
            : `${Math.abs(left)} too many — remove some.`;
        remainingText.classList.toggle("is-balanced", left === 0);
        confirmBtn.disabled = left !== 0;
      }

      rows.forEach((row) => {
        const variantId = row.dataset.variantId;
        const countEl = row.querySelector("[data-count]");
        row.querySelector('[data-action="increment"]').addEventListener("click", () => {
          if (totalSelected() >= remaining) return;
          counts[variantId]++;
          countEl.textContent = counts[variantId];
          refresh();
        });
        row.querySelector('[data-action="decrement"]').addEventListener("click", () => {
          if (counts[variantId] <= 0) return;
          counts[variantId]--;
          countEl.textContent = counts[variantId];
          refresh();
        });
      });

      function cleanup(result) {
        document.body.style.overflow = "";
        modal.remove();
        modalEl = null;
        resolve(result);
      }

      modal.querySelectorAll("[data-close], [data-cancel]").forEach((el) =>
        el.addEventListener("click", () => cleanup(null))
      );

      confirmBtn.addEventListener("click", () => cleanup({ ...counts }));

      refresh();
    });
  }

  // ---------- Checkout interception ----------

  async function handleCheckoutClick(event) {
    if (isProcessing) {
      // A programmatic checkout navigation is already underway — let it through.
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      console.error("Choc promo: couldn't read cart, letting checkout through", e);
      proceedToCheckout();
      return;
    }

    const remaining = computeRemaining(cart);

    if (remaining <= 0) {
      proceedToCheckout();
      return;
    }

    const selections = await showModal(remaining);

    if (!selections) {
      // Customer cancelled — do nothing, let them keep shopping.
      return;
    }

    try {
      await addChocolates(selections);
    } catch (e) {
      console.error("Choc promo: failed to add chocolates", e);
      alert("Sorry, we couldn't add your chocolate choice. Please try again.");
      return;
    }

    proceedToCheckout();
  }

  function proceedToCheckout() {
    isProcessing = true;
    // Shopify's native discount-code URL: applies the code to the checkout
    // session, then forwards the browser on to /checkout. No manual code
    // entry needed. Works for both code-based and automatic discounts, but
    // is only necessary if your discount requires a code.
    const encodedCode = encodeURIComponent(CFG.discountCode);
    window.location.href = `/discount/${encodedCode}?redirect=/checkout`;
  }

  function attachListeners() {
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target.closest(CHECKOUT_SELECTORS.join(","));
        if (target) {
          handleCheckoutClick(event);
        }
      },
      true // capture phase, so we intercept before the theme's own handlers
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachListeners);
  } else {
    attachListeners();
  }
})();